import { useEffect, useRef, useState } from 'react';
import type { Rect } from '../domain/types';

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';

const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const MIN_PX = 4;

/**
 * Rectangular crop over a source image.
 *
 * The crop rectangle is kept in image pixels so it stays meaningful whatever
 * size the preview box happens to be, and is converted to display coordinates
 * only for drawing and hit testing.
 */
export function ImageCropper({
  img,
  crop,
  onChange,
  aspect,
  height = 340,
}: {
  img: HTMLImageElement;
  crop: Rect;
  onChange: (r: Rect) => void;
  /** width / height to lock the crop to, or null for free-form. */
  aspect: number | null;
  height?: number;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  const drag = useRef<{ handle: Handle; startX: number; startY: number; start: Rect } | null>(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const scale = box.w > 0 && box.h > 0 ? Math.min(box.w / iw, box.h / ih) : 0;
  const dispW = iw * scale;
  const dispH = ih * scale;
  const offX = (box.w - dispW) / 2;
  const offY = (box.h - dispH) / 2;

  const toDisplay = (r: Rect) => ({
    left: offX + r.x * scale,
    top: offY + r.y * scale,
    width: r.w * scale,
    height: r.h * scale,
  });

  const clampToImage = (r: Rect): Rect => {
    const w = Math.min(Math.max(MIN_PX, r.w), iw);
    const h = Math.min(Math.max(MIN_PX, r.h), ih);
    return {
      w,
      h,
      x: Math.min(Math.max(0, r.x), iw - w),
      y: Math.min(Math.max(0, r.y), ih - h),
    };
  };

  /** Resize one edge or corner, honouring the aspect lock if there is one. */
  const resized = (handle: Handle, start: Rect, dx: number, dy: number): Rect => {
    let { x, y, w, h } = start;
    const right = start.x + start.w;
    const bottom = start.y + start.h;

    if (handle.includes('w')) {
      x = Math.min(start.x + dx, right - MIN_PX);
      w = right - x;
    }
    if (handle.includes('e')) w = Math.max(MIN_PX, start.w + dx);
    if (handle.includes('n')) {
      y = Math.min(start.y + dy, bottom - MIN_PX);
      h = bottom - y;
    }
    if (handle.includes('s')) h = Math.max(MIN_PX, start.h + dy);

    if (aspect) {
      // Edge handles drive the other dimension; corners follow the width.
      if (handle === 'n' || handle === 's') w = h * aspect;
      else h = w / aspect;
      // Anchors on the left/top edges have to move as the size changes.
      if (handle.includes('w')) x = right - w;
      if (handle.includes('n')) y = bottom - h;
    }
    return clampToImage({ x, y, w, h });
  };

  const onPointerDown = (handle: Handle) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // Not every pointer can be captured; losing capture is not worth
      // dropping the drag over.
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    drag.current = { handle, startX: e.clientX, startY: e.clientY, start: { ...crop } };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || scale === 0) return;
    const dx = (e.clientX - d.startX) / scale;
    const dy = (e.clientY - d.startY) / scale;
    if (d.handle === 'move') {
      onChange(clampToImage({ ...d.start, x: d.start.x + dx, y: d.start.y + dy }));
    } else {
      onChange(resized(d.handle, d.start, dx, dy));
    }
  };

  const endDrag = () => {
    drag.current = null;
  };

  const rect = toDisplay(crop);

  return (
    <div
      className="cropper"
      ref={boxRef}
      style={{ height }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {scale > 0 && (
        <>
          <img
            src={img.src}
            alt=""
            draggable={false}
            style={{ left: offX, top: offY, width: dispW, height: dispH }}
          />
          <div className="crop-rect" style={rect} onPointerDown={onPointerDown('move')}>
            {HANDLES.map((h) => (
              <span key={h} className={`crop-handle h-${h}`} onPointerDown={onPointerDown(h)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** A crop covering the whole image, narrowed to an aspect ratio if given. */
export function fullCrop(img: HTMLImageElement, aspect: number | null = null): Rect {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!aspect) return { x: 0, y: 0, w: iw, h: ih };
  let w = iw;
  let h = iw / aspect;
  if (h > ih) {
    h = ih;
    w = ih * aspect;
  }
  return { x: (iw - w) / 2, y: (ih - h) / 2, w, h };
}
